import { FC, useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Container } from '@/components/ui/Container';
import { aiAppApi, uploadApi } from '@/services/api';
import {
  AIApp,
  CreateAppRequest,
  UpdateAppRequest,
  AppListRequest,
  AppStats,
  AppType,
  AppStatus
} from '@/types';

export const AIAppManagement: FC = () => {
  const [apps, setApps] = useState<AIApp[]>([]);
  const [stats, setStats] = useState<AppStats | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingApp, setEditingApp] = useState<AIApp | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedApps, setSelectedApps] = useState<string[]>([]);

  // 获取应用列表
  const fetchApps = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: AppListRequest = {
        page: currentPage,
        size: pageSize,
        keyword: searchTerm || undefined,
        category: selectedCategory || undefined,
        status: selectedStatus as AppStatus || undefined,
        app_type: selectedType as AppType || undefined,
        sort_by: 'sort_order',
        sort_order: 'desc'
      };

      const response = await aiAppApi.getAppList(params);
      setApps(response.apps || []);
      setTotalCount(response.total || 0);
    } catch (err) {
      console.error('Failed to fetch apps:', err);
      setError('获取应用列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取统计信息
  const fetchStats = async () => {
    try {
      const stats = await aiAppApi.getStats();
      setStats(stats);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // 获取分类列表
  const fetchCategories = async () => {
    try {
      const categories = await aiAppApi.getCategories();
      setCategories(categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchApps();
    fetchStats();
    fetchCategories();
  }, [currentPage, searchTerm, selectedCategory, selectedStatus, selectedType]);

  // 创建应用
  const handleCreateApp = async (data: CreateAppRequest) => {
    try {
      await aiAppApi.createApp(data);
      setShowCreateForm(false);
      await fetchApps();
      await fetchStats();
    } catch (err) {
      console.error('Failed to create app:', err);
      alert('创建应用失败，请稍后重试');
    }
  };

  // 更新应用
  const handleUpdateApp = async (id: string, data: UpdateAppRequest) => {
    try {
      await aiAppApi.updateApp(id, data);
      setEditingApp(null);
      await fetchApps();
      await fetchStats();
    } catch (err) {
      console.error('Failed to update app:', err);
      alert('更新应用失败，请稍后重试');
    }
  };

  // 删除应用
  const handleDeleteApp = async (id: string, name: string) => {
    if (!confirm(`确定要删除应用 "${name}" 吗？此操作不可恢复。`)) return;

    try {
      await aiAppApi.deleteApp(id);
      await fetchApps();
      await fetchStats();
    } catch (err) {
      console.error('Failed to delete app:', err);
      alert('删除应用失败，请稍后重试');
    }
  };

  // 切换应用状态
  const handleToggleStatus = async (app: AIApp) => {
    const newStatus = app.status === 'active' ? 'inactive' : 'active';
    try {
      await aiAppApi.updateApp(app.id, { status: newStatus });
      await fetchApps();
      await fetchStats();
    } catch (err) {
      console.error('Failed to toggle app status:', err);
      alert('更新应用状态失败，请稍后重试');
    }
  };

  // 批量更新状态
  const handleBatchUpdateStatus = async (status: AppStatus) => {
    if (selectedApps.length === 0) {
      alert('请先选择要操作的应用');
      return;
    }

    if (!confirm(`确定要将选中的 ${selectedApps.length} 个应用状态更新为 "${getStatusText(status)}" 吗？`)) return;

    try {
      await aiAppApi.batchUpdateStatus({
        ids: selectedApps,
        status: status
      });
      setSelectedApps([]);
      await fetchApps();
      await fetchStats();
    } catch (err) {
      console.error('Failed to batch update status:', err);
      alert('批量更新状态失败，请稍后重试');
    }
  };

  // 选择/取消选择应用
  const handleSelectApp = (appId: string) => {
    setSelectedApps(prev =>
      prev.includes(appId)
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    if (selectedApps.length === apps.length) {
      setSelectedApps([]);
    } else {
      setSelectedApps(apps.map(app => app.id));
    }
  };

  // 获取状态文本
  const getStatusText = (status: AppStatus) => {
    const statusMap = {
      draft: '草稿',
      active: '激活',
      inactive: '停用',
      archived: '归档'
    };
    return statusMap[status] || status;
  };

  // 获取类型文本
  const getTypeText = (type: AppType) => {
    const typeMap = {
      android: '安卓应用',
      web: '网站工具'
    };
    return typeMap[type] || type;
  };

  
  // 格式化日期
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  if (loading && apps.length === 0) {
    return (
      <Container>
        <div className="py-8">
          <div className="text-center">
            <p className="text-gray-600">加载中...</p>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="py-8">
        {/* 页面标题和操作 */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">AI应用管理</h1>
            <p className="text-gray-600 mt-2">管理和发布AI应用</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowCreateForm(true)}
              className="bg-primary-600 text-white hover:bg-primary-700"
            >
              + 新建应用
            </Button>
            <Button
              onClick={fetchApps}
              className="bg-gray-600 text-white hover:bg-gray-700"
            >
              刷新
            </Button>
          </div>
        </div>

        {/* 统计卡片 */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="p-4">
              <div className="text-sm text-gray-500 mb-1">总应用数</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total_apps}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-500 mb-1">已激活</div>
              <div className="text-2xl font-bold text-green-600">{stats.active_apps}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-500 mb-1">总下载</div>
              <div className="text-2xl font-bold text-blue-600">{stats.total_downloads}</div>
            </Card>
            <Card className="p-4">
              <div className="text-sm text-gray-500 mb-1">总浏览</div>
              <div className="text-2xl font-bold text-purple-600">{stats.total_views}</div>
            </Card>
          </div>
        )}

        {/* 搜索和筛选 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <input
                type="text"
                placeholder="搜索应用..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">所有分类</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">所有状态</option>
                <option value="draft">草稿</option>
                <option value="active">激活</option>
                <option value="inactive">停用</option>
                <option value="archived">归档</option>
              </select>
            </div>
            <div>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">所有类型</option>
                <option value="android">安卓应用</option>
                <option value="web">网站工具</option>
              </select>
            </div>
            <div>
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                  setSelectedStatus('');
                  setSelectedType('');
                  setCurrentPage(1);
                }}
                className="w-full bg-gray-500 text-white hover:bg-gray-600"
              >
                重置筛选
              </Button>
            </div>
          </div>
        </div>

        {/* 批量操作 */}
        {selectedApps.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-800">
                已选择 {selectedApps.length} 个应用
              </span>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleBatchUpdateStatus('active')}
                  size="sm"
                  className="bg-green-600 text-white hover:bg-green-700"
                >
                  批量激活
                </Button>
                <Button
                  onClick={() => handleBatchUpdateStatus('inactive')}
                  size="sm"
                  className="bg-yellow-600 text-white hover:bg-yellow-700"
                >
                  批量停用
                </Button>
                <Button
                  onClick={() => setSelectedApps([])}
                  size="sm"
                  className="bg-gray-500 text-white hover:bg-gray-600"
                >
                  取消选择
                </Button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-800">{error}</p>
            <Button
              onClick={fetchApps}
              className="mt-2 text-sm bg-red-600 text-white hover:bg-red-700"
            >
              重试
            </Button>
          </div>
        )}

        {/* 应用列表 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {/* 表头 */}
          <div className="border-b border-gray-200 p-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedApps.length === apps.length && apps.length > 0}
                onChange={handleSelectAll}
                className="mr-4"
              />
              <div className="grid grid-cols-12 gap-4 flex-1 text-sm font-medium text-gray-700">
                <div className="col-span-4">应用名称</div>
                <div className="col-span-2">分类</div>
                <div className="col-span-1">类型</div>
                <div className="col-span-1">状态</div>
                <div className="col-span-1">下载</div>
                <div className="col-span-1">浏览</div>
                <div className="col-span-2">更新时间</div>
              </div>
            </div>
          </div>

          {/* 应用列表 */}
          <div className="divide-y divide-gray-200">
            {apps.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                暂无应用数据
              </div>
            ) : (
              apps.map((app) => (
                <div key={app.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedApps.includes(app.id)}
                      onChange={() => handleSelectApp(app.id)}
                      className="mr-4"
                    />
                    <div className="grid grid-cols-12 gap-4 flex-1 items-center">
                      {/* 应用名称 */}
                      <div className="col-span-4">
                        <div className="flex items-center gap-3">
                          {app.icon_url && (
                            <img
                              src={app.icon_url}
                              alt={app.name}
                              className="w-10 h-10 rounded-lg object-cover"
                            />
                          )}
                          <div>
                            <div className="font-medium text-gray-900 flex items-center gap-2">
                              {app.name}
                              {app.is_featured && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                  推荐
                                </span>
                              )}
                              {app.is_hot && (
                                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                  热门
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 truncate">
                              {app.description}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 分类 */}
                      <div className="col-span-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded">
                          {app.category || '-'}
                        </span>
                      </div>

                      {/* 类型 */}
                      <div className="col-span-1">
                        <span className={`px-2 py-1 text-sm rounded ${
                          app.app_type === 'android'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {getTypeText(app.app_type)}
                        </span>
                      </div>

                      {/* 状态 */}
                      <div className="col-span-1">
                        <span className={`px-2 py-1 text-sm rounded ${
                          app.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : app.status === 'draft'
                            ? 'bg-gray-100 text-gray-800'
                            : app.status === 'inactive'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {getStatusText(app.status)}
                        </span>
                      </div>

                      {/* 下载量 */}
                      <div className="col-span-1 text-sm text-gray-600">
                        {app.download_count.toLocaleString()}
                      </div>

                      {/* 浏览量 */}
                      <div className="col-span-1 text-sm text-gray-600">
                        {app.view_count.toLocaleString()}
                      </div>

                      {/* 更新时间 */}
                      <div className="col-span-2 text-sm text-gray-600">
                        {formatDate(app.updated_at)}
                      </div>
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => setEditingApp(app)}
                        size="sm"
                        className="text-blue-600 hover:bg-blue-50"
                      >
                        编辑
                      </Button>
                      <Button
                        onClick={() => handleToggleStatus(app)}
                        size="sm"
                        className={`${
                          app.status === 'active'
                            ? 'text-yellow-600 hover:bg-yellow-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {app.status === 'active' ? '停用' : '激活'}
                      </Button>
                      <Button
                        onClick={() => handleDeleteApp(app.id, app.name)}
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                      >
                        删除
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* 分页 */}
          {totalCount > pageSize && (
            <div className="border-t border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  显示 {((currentPage - 1) * pageSize) + 1} 到 {Math.min(currentPage * pageSize, totalCount)} 条，共 {totalCount} 条
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    size="sm"
                    className="bg-gray-500 text-white hover:bg-gray-600 disabled:bg-gray-300"
                  >
                    上一页
                  </Button>
                  <span className="px-3 py-1 text-sm text-gray-700">
                    {currentPage} / {Math.ceil(totalCount / pageSize)}
                  </span>
                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / pageSize), prev + 1))}
                    disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                    size="sm"
                    className="bg-gray-500 text-white hover:bg-gray-600 disabled:bg-gray-300"
                  >
                    下一页
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 创建应用表单模态框 */}
        {showCreateForm && (
          <AppFormModal
            onClose={() => setShowCreateForm(false)}
            onSuccess={(data) => handleCreateApp(data as CreateAppRequest)}
          />
        )}

        {/* 编辑应用表单模态框 */}
        {editingApp && (
          <AppFormModal
            app={editingApp}
            onClose={() => setEditingApp(null)}
            onSuccess={(data) => handleUpdateApp(editingApp.id, data)}
          />
        )}
      </div>
    </Container>
  );
};

// 应用表单模态框组件
interface AppFormModalProps {
  app?: AIApp;
  onClose: () => void;
  onSuccess: (data: CreateAppRequest | UpdateAppRequest) => void;
}

const AppFormModal: FC<AppFormModalProps> = ({ app, onClose, onSuccess }) => {
  // 确保数组字段正确初始化
  const normalizeArray = (data: any): any[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return [];
      }
    }
    return [];
  };

  // 确保config字段正确初始化
  const normalizeConfig = (data: any): Record<string, any> => {
    if (!data) return {};
    if (typeof data === 'object') return data;
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return {};
      }
    }
    return {};
  };

  const [formData, setFormData] = useState<CreateAppRequest>({
    name: app?.name || '',
    description: app?.description || '',
    app_type: app?.app_type || 'android',
    category: app?.category || '',
    developer: app?.developer || '',
    version: app?.version || '',
    size: app?.size || 0,
    download_url: app?.download_url || '',
    external_url: app?.external_url || '',
    icon_url: app?.icon_url || '',
    screenshots: normalizeArray(app?.screenshots),
    tags: normalizeArray(app?.tags),
    config: normalizeConfig(app?.config),
    features: normalizeArray(app?.features),
    slug: app?.slug || '',
    keywords: app?.keywords || '',
    sort_order: app?.sort_order || 0,
    is_featured: app?.is_featured || false,
    is_hot: app?.is_hot || false
  });

  const [tagInput, setTagInput] = useState('');
  const [featureInput, setFeatureInput] = useState('');
  const [screenshotInput, setScreenshotInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<{ [key: string]: boolean }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('请输入应用名称');
      return;
    }

    if (!formData.app_type) {
      alert('请选择应用类型');
      return;
    }

    setLoading(true);
    try {
      // 调试：打印提交的数据
      console.log('提交的应用数据:', {
        ...formData,
        config_type: typeof formData.config,
        config_value: formData.config,
        screenshots_type: typeof formData.screenshots,
        screenshots_value: formData.screenshots
      });

      await onSuccess(formData);
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(t => t !== tag) || []
    }));
  };

  const addFeature = () => {
    if (featureInput.trim() && !formData.features?.includes(featureInput.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...(prev.features || []), featureInput.trim()]
      }));
      setFeatureInput('');
    }
  };

  const removeFeature = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features?.filter(f => f !== feature) || []
    }));
  };

  const addScreenshot = () => {
    if (screenshotInput.trim() && !formData.screenshots?.includes(screenshotInput.trim())) {
      setFormData(prev => ({
        ...prev,
        screenshots: [...(prev.screenshots || []), screenshotInput.trim()]
      }));
      setScreenshotInput('');
    }
  };

  const removeScreenshot = (screenshot: string) => {
    setFormData(prev => ({
      ...prev,
      screenshots: prev.screenshots?.filter(s => s !== screenshot) || []
    }));
  };

  // 文件上传处理函数
  const handleFileUpload = async (file: File, fieldName: string) => {
    if (!file) return;

    setUploading(prev => ({ ...prev, [fieldName]: true }));

    try {
      const result = await uploadApi.uploadFile(file);
      const fullUrl = `${window.location.origin}${result.url}`;

      // 根据字段名更新对应的表单数据
      if (fieldName === 'download_url' || fieldName === 'external_url') {
        setFormData(prev => ({ ...prev, [fieldName]: fullUrl }));
      } else if (fieldName === 'icon_url') {
        setFormData(prev => ({ ...prev, icon_url: fullUrl }));
      }

      alert('文件上传成功！');
    } catch (error) {
      console.error('文件上传失败:', error);
      alert('文件上传失败，请重试');
    } finally {
      setUploading(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            {app ? '编辑AI应用' : '创建AI应用'}
          </h2>
          <Button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 基本信息 */}
          <div>
            <h3 className="text-lg font-semibold mb-4">基本信息</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  应用名称 *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="输入应用名称"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  应用类型 *
                </label>
                <select
                  value={formData.app_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, app_type: e.target.value as AppType }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                >
                  <option value="">选择类型</option>
                  <option value="android">安卓应用</option>
                  <option value="web">网站工具</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  应用分类
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="例如：工具、娱乐、教育"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  开发者
                </label>
                <input
                  type="text"
                  value={formData.developer}
                  onChange={(e) => setFormData(prev => ({ ...prev, developer: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="开发者名称"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  版本号
                </label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="例如：1.0.0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  文件大小 (字节)
                </label>
                <input
                  type="number"
                  value={formData.size}
                  onChange={(e) => setFormData(prev => ({ ...prev, size: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="应用文件大小"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                应用描述
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="输入应用描述"
              />
            </div>
          </div>

          {/* 链接信息 */}
          <div>
            <h3 className="text-lg font-semibold mb-4">链接信息</h3>
            <div className="space-y-4">
              {formData.app_type === 'android' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    下载链接
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={formData.download_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, download_url: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://example.com/app.apk"
                    />
                    <div className="relative">
                      <input
                        type="file"
                        accept=".apk,.ipa,.zip,.rar,.7z,.tar.gz"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(file, 'download_url');
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={uploading['download_url']}
                      />
                      <Button
                        type="button"
                        disabled={uploading['download_url']}
                        className="bg-gray-600 text-white hover:bg-gray-700 whitespace-nowrap"
                      >
                        {uploading['download_url'] ? '上传中...' : '上传文件'}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    支持格式：APK, IPA, ZIP, RAR, 7Z, TAR.GZ
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    外部链接
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={formData.external_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, external_url: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://tool.example.com"
                    />
                    <div className="relative">
                      <input
                        type="file"
                        accept=".html,.htm,.js,.css,.zip,.tar.gz"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(file, 'external_url');
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={uploading['external_url']}
                      />
                      <Button
                        type="button"
                        disabled={uploading['external_url']}
                        className="bg-gray-600 text-white hover:bg-gray-700 whitespace-nowrap"
                      >
                        {uploading['external_url'] ? '上传中...' : '上传文件'}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    支持格式：HTML, JS, CSS, ZIP, TAR.GZ
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  应用图标
                </label>
                <div className="space-y-3">
                  {/* 图标预览 */}
                  {formData.icon_url && (
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-md bg-gray-50">
                      <img
                        src={formData.icon_url}
                        alt="应用图标预览"
                        className="w-12 h-12 rounded-lg object-cover border border-gray-300"
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjQ4IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNCAzMkMxNi4yNjggMzIgMTAgMjUuNzMyIDEwIDIwQzEwIDE0LjI2OCAxNi4yNjggOCAyNCA4QzMxLjczMiA4IDM4IDE0LjI2OCAzOCAyMEMzOCAyNS43MzIgMzEuNzMyIDMyIDI0IDMyWiIgZmlsbD0iIzlDQTNBRiIvPgo8Y2lyY2xlIGN4PSIxOCIgY3k9IjE4IiByPSIyIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNCAyNkMyMS43OTA5IDI2IDIwIDI0LjIwOTEgMjAgMjJDMjAgMTkuNzkwOSAyMS43OTA5IDE4IDI0IDE4QzI2LjIwOTEgMTggMjggMTkuNzkwOSAyOCAyMkMyOCAyNC4yMDkxIDI2LjIwOTEgMjYgMjQgMjZaIiBmaWxsPSIjRjNGNEY2Ii8+Cjwvc3ZnPgo=';
                        }}
                      />
                      <div className="flex-1">
                        <p className="text-sm text-gray-600">当前图标</p>
                        <p className="text-xs text-gray-500 truncate">{formData.icon_url}</p>
                      </div>
                      <Button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, icon_url: '' }))}
                        className="text-red-600 hover:bg-red-50 text-sm"
                      >
                        清除
                      </Button>
                    </div>
                  )}

                  {/* 输入和上传区域 */}
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={formData.icon_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, icon_url: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="https://example.com/icon.png"
                    />
                    <div className="relative">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(file, 'icon_url');
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        disabled={uploading['icon_url']}
                      />
                      <Button
                        type="button"
                        disabled={uploading['icon_url']}
                        className="bg-gray-600 text-white hover:bg-gray-700 whitespace-nowrap"
                      >
                        {uploading['icon_url'] ? '上传中...' : '上传图标'}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    支持格式：PNG, JPG, JPEG, GIF, WEBP，建议尺寸 512x512px
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 标签和特性 */}
          <div>
            <h3 className="text-lg font-semibold mb-4">标签和特性</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  标签
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="输入标签"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button
                    type="button"
                    onClick={addTag}
                    className="bg-gray-600 text-white hover:bg-gray-700"
                  >
                    添加
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded flex items-center gap-1"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  功能特性
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="输入功能特性"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <Button
                    type="button"
                    onClick={addFeature}
                    className="bg-gray-600 text-white hover:bg-gray-700"
                  >
                    添加
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.features?.map((feature) => (
                    <span
                      key={feature}
                      className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded flex items-center gap-1"
                    >
                      {feature}
                      <button
                        type="button"
                        onClick={() => removeFeature(feature)}
                        className="text-green-600 hover:text-green-800"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  应用截图
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="url"
                    value={screenshotInput}
                    onChange={(e) => setScreenshotInput(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="输入截图URL"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addScreenshot())}
                  />
                  <Button
                    type="button"
                    onClick={addScreenshot}
                    className="bg-gray-600 text-white hover:bg-gray-700"
                  >
                    添加
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.screenshots?.map((screenshot) => (
                    <span
                      key={screenshot}
                      className="px-2 py-1 bg-purple-100 text-purple-800 text-sm rounded flex items-center gap-1"
                    >
                      {screenshot.substring(0, 30)}...
                      <button
                        type="button"
                        onClick={() => removeScreenshot(screenshot)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* SEO和设置 */}
          <div>
            <h3 className="text-lg font-semibold mb-4">SEO和设置</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL别名 (Slug)
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="ai-writing-assistant"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SEO关键词
                </label>
                <input
                  type="text"
                  value={formData.keywords}
                  onChange={(e) => setFormData(prev => ({ ...prev, keywords: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="AI,写作,助手"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  排序权重
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="mt-4 flex gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">推荐应用</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_hot}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_hot: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">热门应用</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="bg-gray-300 text-gray-700 hover:bg-gray-400"
            >
              取消
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-primary-600 text-white hover:bg-primary-700 disabled:bg-primary-400"
            >
              {loading ? '保存中...' : '保存'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};